import React, {FC, useState, useEffect} from 'react'
import {Modal, Button, Spinner, Form, Alert} from 'react-bootstrap'
import {clickupApiService} from '../services/clickupApi'
import clickupLogo from './clickup-logo.png'

interface ClickUpOAuthModalProps {
  show: boolean
  onClose: () => void
  onSuccess: (integration: any) => void
}

export const ClickUpOAuthModal: FC<ClickUpOAuthModalProps> = ({
  show,
  onClose,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Estados para configuração do token da API
  const [apiToken, setApiToken] = useState('')
  const [showConfigFields, setShowConfigFields] = useState(false)
  const [isConfiguring, setIsConfiguring] = useState(false)
  const [configError, setConfigError] = useState<string | null>(null)

  // REMOVIDO: useEffect automático que fazia requests desnecessários
  // Agora só mostra os campos de configuração diretamente

  const handleOpenModal = () => {
    setShowConfigFields(true)
    setError('')
    setConfigError(null)
    setApiToken('')
  }

  // Quando o modal abre, mostra os campos diretamente
  useEffect(() => {
    if (show) {
      handleOpenModal()
    }
  }, [show])

  const checkApiToken = async () => {
    try {
      setIsLoading(true)
      setError('')
      setConfigError(null)
      
      const status = await clickupApiService.getStatus()
      
      if (status.is_connected) {
        // Se está conectado, não precisa configurar
        onSuccess(status.integration)
        onClose()
      } else {
        // Se não está conectado, mostra campo para configurar token
        setShowConfigFields(true)
        setIsLoading(false)
      }
    } catch (error: any) {
      console.error('❌ [ClickUp Modal] Erro ao verificar status da integração:', error)
      // Em caso de erro, assume que não tem token e mostra configuração
      setShowConfigFields(true)
      setIsLoading(false)
    }
  }

  const handleSaveApiKeys = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!apiToken.trim()) {
      setConfigError('Por favor, preencha o Token da API')
      return
    }

    try {
      setIsConfiguring(true)
      setConfigError(null)
      
      // Salva o token no backend
      await clickupApiService.saveApiKeys(apiToken.trim())
      
      // Testa a conexão diretamente
      const isConnected = await clickupApiService.testConnection()
      
      if (isConnected) {
        // Se conectou com sucesso, fecha o modal
        onSuccess({ success: true })
        onClose()
      } else {
        setConfigError('Token inválido. Verifique se o token está correto.')
      }
    } catch (error: any) {
      console.error('❌ [ClickUp Modal] Erro ao salvar token da API:', error)
      setConfigError('Erro ao salvar o token da API. Verifique se o token está correto.')
    } finally {
      setIsConfiguring(false)
    }
  }

  const handleClose = () => {
    setError('')
    onClose()
  }

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      size="lg"
      backdrop={isLoading ? 'static' : true}
      keyboard={!isLoading}
    >
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title>
          <div className='d-flex align-items-center'>
            <img 
              src={clickupLogo} 
              alt='ClickUp Logo' 
              style={{width: '24px', height: '24px', marginRight: '8px'}}
            />
            Conectar com ClickUp
          </div>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner animation="border" variant="success" />
            <p className='mt-3 text-muted'>Verificando configuração...</p>
          </div>
        ) : showConfigFields ? (
          <div>
            <div className='mb-4'>
              <img 
                src={clickupLogo} 
                alt='ClickUp Logo' 
                style={{width: '80px', height: '80px', objectFit: 'contain', margin: '0 auto 1rem', display: 'block'}}
              />
              <h5 className='text-center'>Configurar Token da API do ClickUp</h5>
              <p className='text-muted text-center'>
                Para conectar com o ClickUp, você precisa configurar o Token da API.
                <br />
                Após configurar o Token, você poderá autorizar o TaskRush.
              </p>
            </div>
            
            <div className='mb-4'>
                              <Alert variant="info">
                  <i className='fas fa-info-circle me-2'></i>
                  <strong>Como obter o Token:</strong>
                  <ol className='mb-0 mt-2'>
                    <li>Acesse <a href="https://app.clickup.com/settings/apps" target="_blank" rel="noopener noreferrer">https://app.clickup.com/settings/apps</a></li>
                    <li>Faça login na sua conta ClickUp</li>
                    <li>Na seção "Token API", clique em "Copiar"</li>
                    <li>Cole o Token no campo abaixo</li>
                  </ol>
                </Alert>
            </div>
            
            <Form onSubmit={handleSaveApiKeys}>
              <Form.Group className='mb-3'>
                <Form.Label>
                  <strong>API Token</strong> <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  type='text'
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder='Cole seu API Token aqui'
                  disabled={isConfiguring}
                  required
                />
                <Form.Text className='text-muted'>
                  Token de acesso da aplicação ClickUp
                </Form.Text>
              </Form.Group>
              
              {configError && (
                <Alert variant="danger" className='mb-3'>
                  <i className='fas fa-exclamation-triangle me-2'></i>
                  {configError}
                </Alert>
              )}
              
              <div className='d-grid gap-2'>
                <Button 
                  type="submit" 
                  variant="success" 
                  size="lg"
                  disabled={isConfiguring || !apiToken.trim()}
                >
                  {isConfiguring ? (
                    <>
                      <Spinner animation="border" size="sm" className='me-2' />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-save me-2'></i>
                      Salvar Token e Continuar
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="outline-secondary" 
                  onClick={handleClose}
                  disabled={isConfiguring}
                >
                  Cancelar
                </Button>
              </div>
            </Form>
          </div>
        ) : error ? (
          <div className='text-center py-5'>
            <i className='fas fa-exclamation-triangle text-warning fs-1 mb-3'></i>
            <h5 className='text-danger mb-3'>Erro na Autorização</h5>
            <p className='text-muted mb-4'>{error}</p>
            <Button variant="success" onClick={() => checkApiToken()}>
              Tentar Novamente
            </Button>
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  )
}

export default ClickUpOAuthModal
